import { v4 as uuid } from "uuid"

import { call, put, takeEvery, select, fork, delay } from "redux-saga/effects"
import {
  GET_WIDGET_DATA,
  GET_FIELD_INFOS,
  //screenshot
  SCREENSHOT_AND_SAVE_PAGE_THUMBNAIL,
} from "./constants"

// Actions
import { suiviDemandesWidgets } from "Utils/derniereDemandeWidget"
import {
  CODE_MESSAGE_COUPURE,
  DELAI_RELANCE_MS,
  estCoupureConstatee,
  marquerSiCoupure,
  MESSAGE_COUPURE_DEFAUT,
} from "Utils/erreurReseauWidget"
import {
  getWidgetDataSuccess,
  getFieldInfosSuccess,
  changeSavingPageStatusAction,
  // request status
  setWidgetRequestStatus,
} from "./actions"
import { setErrorMessage } from "Containers/Messages/actions"

// SELECTORS
import { selectCurrentToken, selectLangageID } from "Containers/Login/selectors.js"
import { getTranslationString } from "Utils/getTranslation"

import { getCors, postCors, postFileCors } from "Utils/rulesCors"

import {
  responseTypeIsError,
  responseTypeIsSuccess,
  manageErrorGenerator,
  manageSuccessGenerator,
} from "Containers/App/saga"

// Utils
import { config } from "Utils/config"
import request from "Utils/request"
import { formatWidgetFilterBody } from "Utils/widgetsUtils.js"

import { getScreenShotFile } from "Utils/screenshotPage.js"

const logRequestDuration = (reqId, widgetId, start, end) => {
  console.log(`requestId: ${reqId} - widgetId: ${widgetId} took ${parseInt(end - start)} ms.`)
}

const RELANCE_ABANDONNEE = Symbol("relance abandonnée")

/**
 * Appel ETL d'un widget, relancé UNE fois sur coupure réseau (`Utils/erreurReseauWidget`).
 * Pas de relance pour une erreur renvoyée par le serveur, ni pour une requête déjà dépassée.
 * Rend `RELANCE_ABANDONNEE` si une demande plus récente est partie pendant le délai.
 */
function* requeteWidgetAvecUneRelance(widgetId, requestId, url, options) {
  try {
    return yield call(request, url, options)
  } catch (err) {
    // Le marqueur dit au `catch` de la saga que l'échec vient bien du réseau.
    if (!marquerSiCoupure(err) || !suiviDemandesWidgets.estLaDerniere(widgetId, requestId)) throw err
  }
  yield delay(DELAI_RELANCE_MS)
  if (!suiviDemandesWidgets.estLaDerniere(widgetId, requestId)) return RELANCE_ABANDONNEE
  try {
    return yield call(request, url, options)
  } catch (err) {
    marquerSiCoupure(err)
    throw err
  }
}

// Exportée pour les tests : `utils/__tests__/derniereDemandeWidget.test.js`.
export function* getWidgetDataSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const { widgetId, _, body } = payload
  const dashboardId = payload?.dashboardId
  const requestURL = `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/request`
  const requestId = uuid()
  const bodyToUse = { ...body, requestId: requestId }
  const start = performance.now()
  // Cette requête devient la dernière demande du widget : une réponse plus ancienne qui
  // arriverait après elle sera ignorée, sauf si elle répondait à la MÊME requête
  // (`Utils/derniereDemandeWidget`).
  const cleRequete = JSON.stringify(body ?? null)
  suiviDemandesWidgets.noter(widgetId, requestId, cleRequete)

  // Marquer le widget comme en cours de chargement sans bloquer
  yield put(setWidgetRequestStatus({ widgetId: widgetId, status: "PENDING" }))

  try {
    // Une coupure réseau est relancée une fois (`Utils/erreurReseauWidget`).
    const response = yield call(
      requeteWidgetAvecUneRelance,
      widgetId,
      requestId,
      requestURL,
      postCors(bodyToUse, { Authorization: `Bearer ${token}` })
    )
    // Une demande plus récente est arrivée pendant l'attente de la relance : rien à appliquer.
    if (response === RELANCE_ABANDONNEE) return
    const end = performance.now()
    logRequestDuration(requestId, widgetId, start, end)

    // Réponse PÉRIMÉE : une demande DIFFÉRENTE et plus récente est partie pour ce widget. Elle ne
    // touche ni aux données, ni aux options de la liste, ni au statut, et n'affiche aucun message.
    // Une réponse à une requête IDENTIQUE à la dernière demande s'applique, elle : c'est la bonne
    // réponse, arrivée plus tôt (doublon d'ouverture de page).
    if (!suiviDemandesWidgets.estApplicable(widgetId, requestId, cleRequete)) return

    if (responseTypeIsError(response)) {
      console.error("Erreur lors de la récupération des données de widget:", response)
      yield fork(manageErrorGenerator, { response, showMessageToUser: true })
      yield put(setWidgetRequestStatus({ widgetId: widgetId, status: "ERROR" }))
    } else {
      // Gestion du succès sans bloquer les appels simultanés
      yield fork(manageSuccessGenerator, { response, showMessageToUser: false })
      yield put(setWidgetRequestStatus({ widgetId: widgetId, status: "SUCCESS" }))

      /**
       * Une réponse PÉRIMÉE n'écrase ni les données du widget, ni les options de sa liste : elle est
       * écartée plus haut, avant toute écriture. Les filtres dépendants réémettent TOUTES les
       * requêtes d'options à chaque sélection : sans cette garde, deux changements rapprochés
       * suffisaient à laisser une liste sur un état de filtrage qui n'a plus cours.
       *
       * ⚠️ L'ancienne garde comparait l'en-tête `x-request-date`, que l'ETL n'envoie pas : elle
       * laissait tout passer. `requestDate` est conservé pour le réducteur, qui l'ignore s'il est vide.
       */
      const newRequestDate = response["x-request-date"]
      yield put(
        getWidgetDataSuccess({
          widgetId: widgetId,
          columns: response.body.columns,
          data: response.body.data,
          requestDate: newRequestDate,
        })
      )

      if (["dropdownList", "rangeFilter", "searchBar"].includes(payload?.widgetType)) {
        // `multiValueSeparator` : le widget déclare que ses cellules portent plusieurs valeurs
        // jointes, et qu'il faut donc une option par valeur et non par combinaison.
        const bodyToReturn = formatWidgetFilterBody(
          response.body.data,
          payload?.multiValueSeparator,
          payload?.outputToField,
          payload?.optionLabel
        )
        yield put(
          getFieldInfosSuccess({
            widgetId: widgetId,
            body: bodyToReturn,
          })
        )
      }
    }
  } catch (err) {
    const end = performance.now()
    logRequestDuration(requestId, widgetId, start, end)
    // Échec d'une requête déjà dépassée : la demande récente porte le statut du widget.
    if (!suiviDemandesWidgets.estLaDerniere(widgetId, requestId)) return
    console.error(`[route] => ${requestURL} => ERROR => `, err)
    yield put(setWidgetRequestStatus({ widgetId: widgetId, status: "ERROR" }))
    if (estCoupureConstatee(err)) {
      // Coupure persistante après la relance : UN message pour toute la page, dédoublonné par
      // son code — une grappe de coupures en affichait jusqu'à 28.
      const langageId = yield select(selectLangageID)
      const message =
        getTranslationString(CODE_MESSAGE_COUPURE, langageId) || MESSAGE_COUPURE_DEFAUT
      yield put(setErrorMessage({ code: CODE_MESSAGE_COUPURE, message }))
    } else {
      yield put(setErrorMessage(err))
    }
  }
}

///////////////////////////////////////

function* getFieldInfosSaga({ payload }) {
  const token = yield select(selectCurrentToken)

  const specificHeaders = { token: token }

  const { API_URL } = config
  const { widgetId, dataCatalogueName, uuid } = payload
  const encodedUuid = encodeURIComponent(uuid)
  const path = `/v1/datacatalogues/${dataCatalogueName}/fields/${encodedUuid}/info`
  const requestURL = `${API_URL}${path}`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: false })
    else {
      if (responseTypeIsSuccess(response))
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
      yield put(
        getFieldInfosSuccess({
          widgetId: widgetId,
          body: { unique: response.body.unique },
        })
      )
    }
  } catch (err) {
    console.error(`[route] => ${path} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* takeScreenShotThumbnailSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }

  const { API_URL } = config
  const requestURL = API_URL + "/images/upload"

  const { pageId, uploadNumber } = payload

  const file = yield call(() => {
    // La grille a sa hauteur naturelle (c'est la zone d'édition qui défile) : vignette bornée à la
    // hauteur visible, comme quand la grille défilait elle-même.
    const visibleHeight = document.getElementById("dashboardScrollArea")?.clientHeight
    return getScreenShotFile("pageGridLayoutContainer", pageId, visibleHeight)
  })

  const formData = new FormData()
  formData.append("file", file)
  formData.append("uploadType", "pageThumbnail")
  formData.append("pageId", pageId)
  formData.append("uploadNumber", uploadNumber)

  try {
    yield put(changeSavingPageStatusAction("SCREENSHOT_PENDING"))
    const response = yield call(request, requestURL, postFileCors(formData, specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (response?.code === "SUCCESS_TO_UPLOAD_IMAGE") {
        yield put(changeSavingPageStatusAction("SCREENSHOT_DONE"))
      } else {
        yield put(changeSavingPageStatusAction("SCREENSHOT_FAIL"))
      }
      // var imgData = arrayBufferToBase64(response.body.data)
      // window.localStorage.setItem("businessLogo", imgData)
      // if (responseTypeIsSuccess(response)) yield call(manageSuccessGenerator, { response, showMessageToUser: true })
      // var actualDate = parseInt(Date.now()/1000)
      // yield put(uploadBusinessLogoSuccess(actualDate))
    }
  } catch (err) {
    console.error("[route] --> /images/upload | POST | ERROR |", err)
    yield put(setErrorMessage(err))
  }
}

// Images de dashboard (fond de page, widget Image) : envoyées au stockage GRC par la mutation
// `uploadDashboardImage` (lot 5, Pages/Dashboards/graphql/uploadDashboardImage) — plus par
// `/images/upload` (`pageBackground`, `workingWidgets`) sur le disque du back legacy.

function* pageSaga() {
  yield takeEvery(GET_WIDGET_DATA, getWidgetDataSaga)
  yield takeEvery(GET_FIELD_INFOS, getFieldInfosSaga)
  //SCREENSHOT
  yield takeEvery(SCREENSHOT_AND_SAVE_PAGE_THUMBNAIL, takeScreenShotThumbnailSaga)
}

export default pageSaga
